import examAPI from "../examAPI";
import { downloadFile } from "../../utils/download";

// ========== CATEGORIES ==========

export const getCategories = ({
  params: { page, page_size } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .get("/exams/categories/", {
          params: { page, page_size },
          signal,
        })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

export const getCategory = ({
  params: { uuid } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .get(`/exams/categories/${uuid}/`, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

export const createCategory = ({
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .post("/exams/categories/", data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

export const updateCategory = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .put(`/exams/categories/${uuid}/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

export const patchCategory = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .patch(`/exams/categories/${uuid}/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

export const deleteCategory = ({
  params: { uuid } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .delete(`/exams/categories/${uuid}/`, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

// ========== EXAMS ==========

export const getExams = ({
  params: { page, uuid, page_size, status } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .get(`/exams/exams/${uuid ? uuid + "/" : ""}`, {
          params: { page, page_size, status },
          signal,
        })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const createExam = ({
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .post("/exams/exams/", data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const updateExam = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .put(`/exams/exams/${uuid}/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const patchExam = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .patch(`/exams/exams/${uuid}/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const deleteExam = ({
  params: { uuid } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .delete(`/exams/exams/${uuid}/`, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

// An exam to another status (draft, active, archived) → the exam. 400 exam_empty: into active only with a test
// that has questions; an archived exam is read-only until it leaves the archive.
export const changeExamStatus = ({ uuid, status, signal } = {}) =>
  examAPI.post(`/exams/exams/${uuid}/status/`, { status }, { signal }).then((res) => res?.data);

// ========== EXAM SPECIFIC ACTIONS ==========

// The exam's Word in the BTS template. GET word/: what it would print ({success, print, warnings, job}: `job` is the
// live one, if any); nothing is made.
export const getExamWordPreview = (uuid, { signal } = {}) =>
  examAPI.get(`/exams/exams/${uuid}/word/`, { signal }).then((res) => res.data);

// POST word/: the key and the student copy, as the body comes: 201 when both were made within the server's wait
// (document, student_document), 202 while the job still runs (document null, job {uuid, status, stage}).
export const startExamWord = (uuid, { signal } = {}) =>
  examAPI.post(`/exams/exams/${uuid}/word/`, {}, { signal }).then((res) => res.data);

// A Word job as it is now: {uuid, status, stage, error_code, error_detail, warnings, print, document, student_document}.
export const getWordJob = (uuid, { signal } = {}) =>
  examAPI.get(`/exams/word-jobs/${uuid}/`, { signal }).then((res) => res.data);

// Saves an exam document (a row of the list, or a job's document) under the name the server gives (filename* first,
// so that a Cyrillic name stays whole). Rejects with the axios error, its JSON parsed: a 404 document_missing reads
// as a code.
export const downloadExamDocument = (doc, { signal } = {}) =>
  downloadFile(`/exams/documents/${doc.id}/download/`, { fallbackName: doc.filename || "exam.docx", signal });

// The exam's documents ({success, exam, documents, total_documents}), newest first. A row carries kind (key |
// student), pair, template (bts | classic), language, code and job; rejects with the axios error.
export const getExamDocuments = ({ params: { uuid } = {}, signal } = {}) =>
  examAPI.get(`/exams/exams/${uuid}/documents/`, { signal }).then((res) => res.data);

// The exam as bilorg's variant structure file (answer keys, points, QB question ids), saved under the name the server
// gives. Rejects with the axios error, its JSON body parsed: a 403 answers_hidden (the exam has questions whose
// answers the user may not read) reads as a code.
export const downloadExamBilorgFile = ({ uuid, signal } = {}) =>
  downloadFile(`/exams/exams/${uuid}/questions/export_excel/`, { fallbackName: "exam_bilorg.xlsx", signal });

export const getExamTests = ({
  params: { uuid } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .get(`/exams/exams/${uuid}/tests/`, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

export const addTestToExam = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .post(`/exams/exams/${uuid}/add_test/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          // Исправлено: передаем только ошибку
          reject(err);  
        });
    } catch (error) {
      // Исправлено: передаем только ошибку  
      reject(error);
    }
  });
};

export const removeTestFromExam = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .delete(`/exams/exams/${uuid}/remove_test/`, { data, signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const reorderExamTests = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .patch(`/exams/exams/${uuid}/reorder_tests/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const getExamStatistics = ({
  params: { uuid } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .get(`/exams/exams/${uuid}/statistics/`, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

export const getExamsBySubject = ({
  params: { subject_id, page, page_size } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .get("/exams/exams/by_subject/", {
          params: { subject_id, page, page_size },
          signal,
        })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

// ========== TESTS ==========

export const getTests = ({
  params: { page, uuid, page_size, ordering, search } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .get(`/exams/tests/${uuid ? uuid + "/" : ""}`, {
          params: { page, page_size, ordering, search },
          signal,
        })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const createTest = ({
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .post("/exams/tests/", data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const updateTest = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .put(`/exams/tests/${uuid}/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

export const patchTest = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .patch(`/exams/tests/${uuid}/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const deleteTest = ({
  params: { uuid } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .delete(`/exams/tests/${uuid}/`, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

// ========== TEST SPECIFIC ACTIONS ==========

export const getTestQuestions = ({
  params: { uuid } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .get(`/exams/tests/${uuid}/questions/`, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const addQuestionToTest = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .post(`/exams/tests/${uuid}/add_question/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const uploadTestAdditionalInformation = ({
  params: { uuid } = {},
  file,
  text,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      const url = `/exams/tests/${uuid}/upload_additional_information/`;
      if (file) {
        const formData = new FormData();
        formData.append("file", file);
        examAPI
          .post(url, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            signal,
          })
          .then((res) => resolve(res?.data))
          .catch((err) => reject(err));
      } else {
        examAPI
          .post(url, { text: text ?? "" }, {
            headers: { "Content-Type": "application/json" },
            signal,
          })
          .then((res) => resolve(res?.data))
          .catch((err) => reject(err));
      }
    } catch (error) {
      reject(error);
    }
  });
};

export const addMultipleQuestionsToTest = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .post(`/exams/tests/${uuid}/add_multiple_questions/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

// Gathers the questions of a passage into one block whose first row lands at `data.order` (1-based,
// never inside another passage's block). Resolves to [{uuid, order}] for every row of the test.
export const movePassageInTest = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .post(`/exams/tests/${uuid}/move_passage/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

// Removes the questions of a passage (`data.passage`) from this test only, like deleting each row.
export const removePassageFromTest = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .post(`/exams/tests/${uuid}/remove_passage/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const removeQuestionFromTest = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .delete(`/exams/tests/${uuid}/remove_question/`, { data, signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const getActiveTests = ({
  params: { page, page_size } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .get("/exams/tests/active/", {
          params: { page, page_size },
          signal,
        })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

// ========== TEST QUESTIONS ==========

export const getTestQuestionsList = ({
  params: { page, page_size } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .get("/exams/test-questions/", {
          params: { page, page_size },
          signal,
        })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

export const getTestQuestion = ({
  params: { uuid } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .get(`/exams/test-questions/${uuid}/`, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

export const createTestQuestion = ({
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .post("/exams/test-questions/", data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

export const updateTestQuestion = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .put(`/exams/test-questions/${uuid}/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject("axios error", err);
        });
    } catch (error) {
      reject("system error", error);
    }
  });
};

export const patchTestQuestion = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .patch(`/exams/test-questions/${uuid}/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const deleteTestQuestion = ({
  params: { uuid } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .delete(`/exams/test-questions/${uuid}/`, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const reorderTestQuestion = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .patch(`/exams/test-questions/${uuid}/reorder/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

// ========== EXAM TESTS ==========

export const getExamTestsList = ({
  params: { page, page_size } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .get("/exams/exam-tests/", {
          params: { page, page_size },
          signal,
        })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const getExamTest = ({
  params: { uuid } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .get(`/exams/exam-tests/${uuid}/`, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const createExamTest = ({
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .post("/exams/exam-tests/", data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const updateExamTest = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .put(`/exams/exam-tests/${uuid}/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const patchExamTest = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .patch(`/exams/exam-tests/${uuid}/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const deleteExamTest = ({
  params: { uuid } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .delete(`/exams/exam-tests/${uuid}/`, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const updateExamTestReorder = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .patch(`/exams/exam-tests/${uuid}/reorder/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};


export const updateExamTestWeight = ({
  params: { uuid } = {},
  data,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .patch(`/exams/exam-tests/${uuid}/update_weight/`, data, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};

export const toggleExamTestRequired = ({
  params: { uuid } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      examAPI
        .patch(`/exams/exam-tests/${uuid}/toggle_required/`, {}, { signal })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (error) {
      reject(error);
    }
  });
};